"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const L = {
    fr: {
        title: "Découpez une phrase en jetons",
        placeholder: "Tapez ou collez du texte...",
        sample: "L'automatisation change tout pour une petite entreprise.",
        reset: "Remettre l'exemple",
        words: "mots",
        tokens: "jetons",
        ratio: "jetons par mot",
        chars: "caractères",
        warning:
            "Estimation, pas un comptage exact. Chaque modèle a son propre découpage : ce simulateur reproduit le principe et donne le bon ordre de grandeur, pas le chiffre de votre fournisseur.",
    },
    en: {
        title: "Cut a sentence into tokens",
        placeholder: "Type or paste some text...",
        sample: "Automation changes everything for a small business.",
        reset: "Restore the example",
        words: "words",
        tokens: "tokens",
        ratio: "tokens per word",
        chars: "characters",
        warning:
            "An estimate, not an exact count. Every model has its own splitting: this simulator reproduces the principle and gives the right order of magnitude, not your provider's figure.",
    },
};

/**
 * Découpage approximatif, à but pédagogique.
 *
 * Le principe reproduit est le bon : les mots courts et fréquents font un seul
 * jeton, les mots longs se coupent en morceaux, la ponctuation compte à part.
 * Un vrai tokenizer repose sur un vocabulaire appris de plusieurs dizaines de
 * milliers d'entrées, impossible à embarquer dans une page sans l'alourdir.
 */
const SPLIT = {
    // Calibre pour retrouver les ratios reels : environ 1,7 jeton par mot en
    // francais, 1,3 en anglais. Les deux langues ne se decoupent pas pareil,
    // ce que le terme explique juste au-dessus de la demo.
    fr: { threshold: 6, chunk: 4 },
    en: { threshold: 8, chunk: 5 },
};

function approximateTokens(text, lang) {
    const { threshold, chunk } = SPLIT[lang] || SPLIT.fr;
    const pieces = text.match(/\s+|[\p{L}\p{N}]+|[^\s\p{L}\p{N}]/gu) || [];
    const tokens = [];

    for (const piece of pieces) {
        if (/^\s+$/.test(piece)) continue;

        const isWord = /^[\p{L}\p{N}]+$/u.test(piece);

        if (isWord && piece.length > threshold) {
            for (let i = 0; i < piece.length; i += chunk) {
                tokens.push(piece.slice(i, i + chunk));
            }
        } else {
            tokens.push(piece);
        }
    }

    return tokens;
}

export default function TokenCounter({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [text, setText] = useState(t.sample);

    const { tokens, words } = useMemo(() => {
        const w = text.trim() ? text.trim().split(/\s+/).length : 0;
        return { tokens: approximateTokens(text, lang), words: w };
    }, [text, lang]);

    const ratio = words > 0 ? (tokens.length / words).toFixed(1) : "0";

    const stat = (value, label) => (
        <div className="text-center px-3">
            <div className="text-2xl font-bold text-primary tabular-nums">
                {value}
            </div>
            <div className="text-xs text-gray-600 dark:text-lightBlue mb-0">
                {label}
            </div>
        </div>
    );

    return (
        <div className="my-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5 sm:p-6 text-base leading-normal">
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <SolarIcon
                        name="text-square-linear"
                        className="w-5 h-5 text-primary"
                    />
                    <div className="font-bold dark:text-white text-base leading-snug">
                        {t.title}
                    </div>
                </div>
                {text !== t.sample && (
                    <button
                        type="button"
                        onClick={() => setText(t.sample)}
                        className="text-xs text-primary hover:text-primary-100 shrink-0"
                    >
                        {t.reset}
                    </button>
                )}
            </div>

            <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 600))}
                placeholder={t.placeholder}
                rows={3}
                className="w-full rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-3 text-sm dark:text-white resize-none focus:outline-hidden focus:border-primary/60"
            />

            <div className="flex flex-wrap gap-1.5 mt-4 min-h-[2.5rem]">
                {tokens.map((token, i) => (
                    <span
                        key={`${token}-${i}`}
                        className="px-2 py-1 rounded-md bg-primary/10 dark:bg-primary/20 border border-primary/25 font-mono text-xs dark:text-white"
                    >
                        {token}
                    </span>
                ))}
            </div>

            <div className="flex items-center justify-center gap-2 mt-5 py-3 rounded-xl bg-primary/[0.06] dark:bg-primary/10 divide-x divide-primary/15">
                {stat(words, t.words)}
                {stat(tokens.length, t.tokens)}
                {stat(ratio, t.ratio)}
                {stat(text.length, t.chars)}
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-4 mb-0">
                {t.warning}
            </p>
        </div>
    );
}
